import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { map, Observable, of, shareReplay } from 'rxjs';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { PIN_COLORS } from 'src/app/shared/constants/map-pin.constant';
import {
  IMapTripDestination,
  IParsedTripLeg,
} from 'src/app/core/types/interfaces/trip-elements.interface';

import { Trip } from 'src/app/shared/classes/trip';

import {
  SavedTripDisplayModel,
  TripsService,
} from 'src/app/core/services/trips.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { CITY_SERVICES, CityProviders } from 'src/app/core/utils/city-services';
import { IFavoriteItem } from 'src/app/core/services/favorites-provider.service';
import { TripWizardStateService } from 'src/app/shared/components/trip-wizard/trip-wizard-state.service';
import { SchedulerBlockNavigationService } from 'src/app/core/services/scheduler-block-navigation.service';

import { PreviewTripSheetComponent } from '../../preview-trip-sheet/preview-trip-sheet.component';
import { TripPlanPrintComponent } from 'src/app/shared/components/trip-plan-print/trip-plan-print.component';

import moment from 'moment';

@Component({
  selector: 'app-short-trip-info',
  templateUrl: './short-trip-info.component.html',
  styleUrls: ['./short-trip-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShortTripInfoComponent implements OnInit, OnChanges {
  @Input() trip: any;
  @Input() favourites: IFavoriteItem[] = [];
  @Input() isFetching: boolean = false;
  @Output() remove: EventEmitter<any> = new EventEmitter<any>();
  @Output() shareTrip: EventEmitter<any> = new EventEmitter<any>();

  extraTripInfo: {
    regions: string[];
    firstCityImgUrl: string;
    allRoutes: string;
  } = {
    regions: [],
    allRoutes: '',
    firstCityImgUrl: '',
  };
  duration: string = '';
  destinations: any[] = [];
  isMoreTripInfoLoading: boolean = false;

  public readonly PIN_COLORS: string[] = PIN_COLORS;
  public readonly SERVICES = CITY_SERVICES.slice(1);

  public destinationsRoute: IMapTripDestination[] = [];
  public legs: IParsedTripLeg[] = [];
  public favoritesAmount: Observable<number>;
  @ViewChild('printElementRef', { static: false })
  printElementRef: TripPlanPrintComponent;

  protected readonly eSvgIcons = ESvgIcons;
  protected readonly eCityProviders = CityProviders;

  #router = inject(Router);
  #tripWizardStateService = inject(TripWizardStateService);
  #schedulerBlockNavigationService = inject(SchedulerBlockNavigationService);

  constructor(
    public breakpointService: BreakpointService,
    private cdr: ChangeDetectorRef,
    private tripsService: TripsService,
    private matBottomSheet: MatBottomSheet,
  ) {}

  ngOnInit(): void {
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.favourites) {
      this.initData();
    }
  }

  public navigateToUserWishList(): void {
    this.#router.navigate(['account', 'my-wish-list'], {
      queryParams: { id: this.trip.visible_id },
    });
  }

  public navigateToTripPlanner(
    trip: SavedTripDisplayModel,
    provider: CityProviders,
  ) {
    this.#tripWizardStateService.currentTripState =
      Trip.CreateTripBasedOnSavedTripModel(trip);
    this.#schedulerBlockNavigationService.scheduleNavigation(0, provider);
    this.#router.navigate(['trip-planner']);
  }

  public setParsedTripLegs(legs: IParsedTripLeg[]): void {
    this.legs = legs;
    this.cdr.detectChanges();
  }

  public getCityName(destination: string): string {
    if (!destination) return '';
    return destination.split(',')[0];
  }

  public setDestinationsRoute(trip: any): void {
    if (!trip) return;
    const { destinations, route } = trip;

    let allRoutesString = '';
    this.destinationsRoute = [];
    route.forEach((r: any, index: number): void => {
      const parsedCityCoordinate = r.coordinates
        .split(',')
        .map((cs: string) => parseFloat(cs.trim()))
        .reverse();
      this.destinationsRoute.push({
        coordinates: parsedCityCoordinate,
        nextLegIsFlight: destinations[index].nextLegIsFlight,
      });
      if (index === 0) {
        this.extraTripInfo.firstCityImgUrl = r.imageUrl;
      }

      allRoutesString += `${r.destName}`;
      if (index !== route.length - 1) {
        allRoutesString += ' | ';
      }

      const locationInfo = r.region
        ? `${r.region}, ${r.countryName}`
        : r.countryName;
      this.extraTripInfo.regions.push(locationInfo);
    });

    this.extraTripInfo.allRoutes = allRoutesString;
    this.setFavouritesChange(trip);
  }

  public showMobileTripOverview(): void {
    const triggerByCallback = (): void => {
      const bottomSheetRef = this.matBottomSheet.open(
        PreviewTripSheetComponent,
        {
          hasBackdrop: false,
          closeOnNavigation: true,
          panelClass: 'custom-bottom-sheet',
          data: {
            trip: this.trip,
            duration: this.duration,
            extraTripInfo: this.extraTripInfo,
            tripDestinations: this.destinations,
            destinationsRoute: this.destinationsRoute,
          },
        },
      );
      bottomSheetRef.instance.actionEmitter.subscribe((action) => {
        switch (action) {
          case 'Share':
            this.shareTrip.next(this.trip);
            break;
          case 'Print':
            this.onPrintTripOverviewHandler();
            break;
          case 'Delete':
            this.remove.next(this.trip);
            bottomSheetRef.instance.dismissPreviewTrip();
            break;
        }
      });
    };
    this.loadMoreTripInfo(() => triggerByCallback());
  }

  onPrintTripOverviewHandler(): void {
    const triggerByCallback = async (): Promise<void> => {
      const waitForElement = async (): Promise<void> => {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            this.cdr.markForCheck();
            if (this.printElementRef) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      };
      await waitForElement();

      if (this.printElementRef) {
        this.printElementRef.tripPlanPrint();
      }
    };

    this.loadMoreTripInfo(() => triggerByCallback());
  }

  private initData(): void {
    if (this.trip) {
      this.setDestinationsRoute(this.trip);
      this.setDurationString(this.trip);
    }
  }

  private setFavouritesChange(trip: Trip): void {
    const { route } = trip;
    this.favoritesAmount = of(this.favourites).pipe(
      map((items) => {
        const cityIds = route.map((i: any) => i.id);
        return items.filter((i) => cityIds.includes(i.place_id)).length;
      }),
      shareReplay(1),
    );
  }

  private setDurationString(trip: any): void {
    const { date_start, date_end, duration } = trip;
    if (moment(date_start).isValid() && moment(date_end).isValid()) {
      this.duration = `${moment(date_start).format('ddd. MMM DD') + ' - ' + moment(date_end).format('ddd. MMM DD')}. ${duration} days`;
    } else {
      this.duration = `${duration} days`;
    }
  }

  private loadMoreTripInfo(callback: () => void): void {
    if (this.destinations.length) {
      callback();
      return;
    }

    this.isMoreTripInfoLoading = true;
    this.tripsService.createTempItinerary(this.trip).subscribe({
      next: (res) => {
        if (res && res.destinations) {
          this.destinations = res.destinations;
          setTimeout(() => callback(), 250);
        }
      },
      complete: () => (this.isMoreTripInfoLoading = false),
    });
  }
}
